import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { Avatar, Button, PopoverMenu, Text, Truncated, usePopoverMenu } from '@frontend/design-system';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';

type Props = {
  name: string;
  locationName: string;
  primaryMeta?: ReactNode;
  secondaryMeta?: ReactNode;
  menuItems: ReactNode;
  locationChip?: (locationName: string) => React.ReactNode;
  getTriggerProps: ReturnType<typeof usePopoverMenu>['getTriggerProps'];
  getMenuProps: ReturnType<typeof usePopoverMenu>['getMenuProps'];
  isWidgetListItem?: boolean;
};

export const ScheduleRequestItemHeaderUI = ({
  name,
  locationName,
  primaryMeta,
  secondaryMeta,
  menuItems,
  locationChip,
  getTriggerProps,
  getMenuProps,
  isWidgetListItem,
}: Props) => (
  <>
    <div css={containerStyles}>
      <div css={requestInfoStyle}>
        <Avatar name={name} size='large' />
        <div css={requestInfoTextStyle}>
          <div css={primaryInfoStyles(isWidgetListItem)}>
            <Truncated as={Text} css={nameStyle(isWidgetListItem)} weight='bold'>
              {name}
            </Truncated>
            {primaryMeta}
          </div>
          <div css={secondaryInfoStyles}>{secondaryMeta}</div>
        </div>
      </div>

      <div css={actionsStyles}>
        {locationChip && typeof locationChip === 'function' && locationChip(locationName)}
        <Button
          variant='secondary'
          iconName='more-small'
          trackingId={ScheduleRequestsTrackingIds.requestListItemMoreBtn}
          {...getTriggerProps()}
        />
        <PopoverMenu {...getMenuProps()}>{menuItems}</PopoverMenu>
      </div>
    </div>
  </>
);

const containerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  gap: var(--fabric-space-gap-md);
`;

const requestInfoStyle = css`
  display: flex;
  align-items: center;
  flex: 1;
  gap: var(--fabric-space-gap-sm);
`;

const requestInfoTextStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
`;

const primaryInfoStyles = (isWidgetItem = false) => css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
  ${!isWidgetItem && 'max-width: 260px;'}
  ${isWidgetItem && 'flex-wrap: wrap;'}
`;

const secondaryInfoStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const actionsStyles = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
`;

const nameStyle = (isWidgetItem = false) => css`
  ${isWidgetItem && 'max-width: 220px; width: auto;'}
`;
